/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { ROUTES } from './routes';
import { ThemeProvider, useTheme } from './theme';
import { Search, MapPin, Menu, PlusCircle, User as UserIcon, LogIn, UserPlus, Layout, ShoppingBag, Store, MessageSquare, Settings as SettingsIcon, ShieldCheck, Navigation } from 'lucide-react';
import Breadcrumbs from './components/Breadcrumbs';
import BottomNav from './components/BottomNav';
import LocationModal from './components/LocationModal';
import CategoryModal from './components/CategoryModal';
import Hero from './components/Hero';
import Trending from './components/Trending';
import StorefrontSection from './components/StorefrontSection';
import RecentlyListed from './components/RecentlyListed';
import SponsoredAds from './components/SponsoredAds';
import StripeOnboarding from './components/StripeOnboarding';
import SellerPlanSelection from './components/SellerPlanSelection';
import AdminSettings from './pages/AdminSettings';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import ManageItems from './pages/ManageItems';
import AddItem from './pages/AddItem';
import StorefrontSettings from './pages/StorefrontSettings';
import ItemDetail from './pages/ItemDetail';
import Storefront from './pages/Storefront';
import Inbox from './pages/Inbox';
import MessageThread from './pages/MessageThread';
import AdManagement from './pages/AdManagement';
import AdminDashboard from './pages/AdminDashboard';
import SearchPage from './pages/Search';
import { useNavigate } from 'react-router-dom';
import { getCurrentPosition } from './utils/geolocation';

// Location context to provide coordinates throughout the app
interface LocationContextType {
  zip: string;
  radius: number;
  latitude?: number;
  longitude?: number;
  setLocation: (loc: { zip: string; radius: number; latitude?: number; longitude?: number }) => void;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) throw new Error('useLocation must be used within a LocationProvider');
  return context;
};

const App: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        console.error("Failed to parse user data", e);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.href = ROUTES.LOGIN;
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  const isAdmin = user?.userType === 'Admin';

  return (
    <ThemeProvider>
      <LocationProvider>
        <Router>
          <AppContent user={user} isAdmin={isAdmin} handleLogout={handleLogout} />
        </Router>
      </LocationProvider>
    </ThemeProvider>
  );
};

const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [location, setLocationState] = useState<{
    zip: string;
    radius: number;
    latitude?: number;
    longitude?: number;
  }>(() => {
    const saved = localStorage.getItem('userLocation');
    return saved ? JSON.parse(saved) : { zip: '90210', radius: 25 };
  });

  const setLocation = (newLoc: any) => {
    setLocationState(newLoc);
    localStorage.setItem('userLocation', JSON.stringify(newLoc));
  };

  useEffect(() => {
    // Only auto-detect if we don't have lat/lng yet
    if (!location.latitude || !location.longitude) {
      getCurrentPosition()
        .then(coords => {
          setLocation({
            ...location,
            latitude: coords.latitude,
            longitude: coords.longitude
          });
        })
        .catch(err => console.log('Geolocation auto-detection failed or denied:', err));
    }
  }, []);

  return (
    <LocationContext.Provider value={{ ...location, setLocation }}>
      {children}
    </LocationContext.Provider>
  );
};

interface AppContentProps {
  user: any;
  isAdmin: boolean;
  handleLogout: () => void;
}

const AppContent: React.FC<AppContentProps> = ({ user, isAdmin, handleLogout }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.colors.background, color: theme.colors.text, fontFamily: theme.typography.fontFamily }}>
      <nav className="shadow-sm border-b sticky top-0 z-50 transition-all duration-300" style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
        <div className="max-w-7xl mx-auto px-4 h-16 md:h-20 flex items-center justify-between gap-4">
          {/* LOGO */}
          <Link to={ROUTES.HOME} className="flex-shrink-0 flex items-center gap-2">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center text-white font-bold text-xl" style={{ backgroundColor: theme.colors.primary }}>H</div>
            <span className="text-xl md:text-2xl font-black hidden lg:block tracking-tighter" style={{ color: theme.colors.text }}>Hometown Market</span>
          </Link>

          {/* 🔍 Search items, shops... */}
          <div className={`flex-grow transition-all duration-300 ${isSearchFocused ? 'max-w-2xl' : 'max-w-md'}`}>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search size={18} className="group-focus-within:text-blue-500 transition-colors" style={{ color: theme.colors.mutedText }} />
              </div>
              <input
                type="text"
                placeholder="Search items, shops..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    navigate(`${ROUTES.SEARCH}?q=${encodeURIComponent(searchQuery)}`);
                  }
                }}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                className="block w-full pl-12 pr-4 py-2.5 md:py-3 border rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all text-sm md:text-base"
                style={{ 
                  backgroundColor: theme.colors.background, 
                  borderColor: theme.colors.border, 
                  color: theme.colors.text
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-6">
            {/* 📍 Location ▼ */}
            <button 
              onClick={() => setIsLocationModalOpen(true)}
              className="hidden sm:flex items-center gap-1.5 hover:opacity-80 transition-opacity font-bold text-sm" 
              style={{ color: theme.colors.text }}
            >
              <MapPin size={20} className="text-blue-500" />
              <span>{location.zip} (+{location.radius}mi)</span>
              <span className="text-[10px] opacity-50">▼</span>
            </button>

            {/* ☰ Categories ▼ */}
            <button 
              onClick={() => setIsCategoryModalOpen(true)}
              className="hidden sm:flex items-center gap-1.5 hover:opacity-80 transition-opacity font-bold text-sm" 
              style={{ color: theme.colors.text }}
            >
              <Menu size={20} />
              <span>Categories</span>
              <span className="text-[10px] opacity-50">▼</span>
            </button>

            {/* Sell */}
            <Link to={user ? ROUTES.ADD_LISTING : ROUTES.LOGIN} className="hidden md:flex items-center gap-1.5 hover:opacity-80 transition-opacity font-bold text-sm" style={{ color: theme.colors.primary }}>
              <PlusCircle size={20} />
              <span>Sell</span>
            </Link>

            {/* Login / Sign Up or Profile */}
            {user ? (
              <div className="relative group">
                <button className="flex items-center gap-1 hover:opacity-80" style={{ color: theme.colors.text }}>
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl border flex items-center justify-center transition-all group-hover:shadow-md" style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.background }}>
                    <UserIcon size={18} />
                  </div>
                  <span className="hidden md:block font-bold text-sm ml-1">Account</span>
                  <span className="text-[10px] opacity-50 ml-0.5">▼</span>
                </button>
                <div className="absolute right-0 w-56 mt-2 py-3 rounded-2xl shadow-2xl border hidden group-hover:block z-50 animate-in fade-in slide-in-from-top-2 duration-200" 
                     style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
                  <div className="px-4 pb-3 mb-2 border-b" style={{ borderColor: theme.colors.border }}>
                    <p className="text-xs font-black uppercase tracking-widest opacity-40 mb-1">Signed in as</p>
                    <p className="font-bold text-sm truncate" style={{ color: theme.colors.text }}>{user.email}</p>
                  </div>
                  <Link to={ROUTES.DASHBOARD} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 transition-colors font-bold text-sm" style={{ color: theme.colors.text }}>
                    <Layout size={16} className="opacity-40" /> Dashboard
                  </Link>
                  <Link to={ROUTES.LISTINGS} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 transition-colors font-bold text-sm" style={{ color: theme.colors.text }}>
                    <ShoppingBag size={16} className="opacity-40" /> My Listings
                  </Link>
                  <Link to={`/store/${user.id}`} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 transition-colors font-bold text-sm" style={{ color: theme.colors.text }}>
                    <Store size={16} className="opacity-40" /> Public Storefront
                  </Link>
                  <Link to={ROUTES.MESSAGES} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 transition-colors font-bold text-sm" style={{ color: theme.colors.text }}>
                    <MessageSquare size={16} className="opacity-40" /> Messages
                  </Link>
                  <Link to={ROUTES.SETTINGS} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 transition-colors font-bold text-sm" style={{ color: theme.colors.text }}>
                    <SettingsIcon size={16} className="opacity-40" /> Settings
                  </Link>
                  {isAdmin && (
                    <>
                      <div className="border-t my-2" style={{ borderColor: theme.colors.border }} />
                      <Link to="/admin" className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 transition-colors font-bold text-sm" style={{ color: theme.colors.primary }}>
                        <ShieldCheck size={16} /> Admin Panel
                      </Link>
                    </>
                  )}
                  <div className="border-t my-2" style={{ borderColor: theme.colors.border }} />
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full text-left px-4 py-2 hover:bg-red-50 transition-colors font-bold text-sm"
                    style={{ color: theme.colors.danger }}
                  >
                    <LogIn size={16} className="rotate-180" /> Logout
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <Link to={ROUTES.LOGIN} className="hidden sm:flex items-center gap-1 hover:opacity-80" style={{ color: theme.colors.text }}>
                  <LogIn size={18} />
                  <span>Login</span>
                </Link>
                <Link to={ROUTES.SIGNUP} className="px-4 py-2 rounded-full text-white font-medium hover:opacity-90 flex items-center gap-1" style={{ backgroundColor: theme.colors.primary }}>
                  <UserPlus size={18} />
                  <span>Sign Up</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16 md:mb-0">
        <div className="py-6">
          <Breadcrumbs />
        </div>
        <Routes>
          <Route path={ROUTES.HOME} element={
            <div className="space-y-4">
              <Hero />
              <Trending />
              <StorefrontSection />
              <RecentlyListed />
              <SponsoredAds />
            </div>
          } />
          <Route path={ROUTES.LOGIN} element={user ? <Navigate to={ROUTES.DASHBOARD} /> : <Login />} />
          <Route path={ROUTES.SIGNUP} element={user ? <Navigate to={ROUTES.DASHBOARD} /> : <Register />} />
          <Route path="/register" element={<Navigate to={ROUTES.SIGNUP} replace />} />
          
          <Route path="/onboard" element={<StripeOnboarding />} />
          <Route path="/plans" element={<SellerPlanSelection />} />
          
          <Route path={ROUTES.DASHBOARD} element={user ? <Dashboard /> : <Navigate to={ROUTES.LOGIN} />} />
          <Route path={ROUTES.LISTINGS} element={user ? <ManageItems /> : <Navigate to={ROUTES.LOGIN} />} />
          <Route path={ROUTES.ADD_LISTING} element={user ? <AddItem /> : <Navigate to={ROUTES.LOGIN} />} />
          <Route path={ROUTES.EDIT_LISTING} element={user ? <AddItem /> : <Navigate to={ROUTES.LOGIN} />} />
          <Route path={ROUTES.STOREFRONT_SETTINGS} element={user ? <StorefrontSettings /> : <Navigate to={ROUTES.LOGIN} />} />
          <Route path={ROUTES.MESSAGES} element={user ? <Inbox /> : <Navigate to={ROUTES.LOGIN} />} />
          <Route path={ROUTES.MESSAGES + "/:id"} element={user ? <MessageThread /> : <Navigate to={ROUTES.LOGIN} />} />
          <Route path={ROUTES.PAYMENTS} element={user ? <div className="p-8 rounded-3xl border text-center font-bold" style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>Payments integration coming soon...</div> : <Navigate to={ROUTES.LOGIN} />} />
          <Route path={ROUTES.ADS} element={user ? <AdManagement /> : <Navigate to={ROUTES.LOGIN} />} />
          <Route path={ROUTES.SETTINGS} element={user ? <div className="p-8 rounded-3xl border text-center font-bold" style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>User Settings Page coming soon...</div> : <Navigate to={ROUTES.LOGIN} />} />

          <Route path={ROUTES.SEARCH} element={<SearchPage />} />
          <Route path={ROUTES.ITEM} element={<ItemDetail />} />
          <Route path={ROUTES.STOREFRONT} element={<Storefront />} />

          <Route path="/admin" element={isAdmin ? <AdminDashboard /> : <Navigate to="/" />} />
          <Route path="/admin/settings" element={isAdmin ? <AdminSettings /> : <Navigate to="/" />} />

          <Route path="*" element={<div className="text-center py-20"><h2 className="text-2xl font-bold" style={{ color: theme.colors.text }}>Page Not Found</h2></div>} />
        </Routes>
      </main>
      <BottomNav />
      <LocationModal 
        isOpen={isLocationModalOpen} 
        onClose={() => setIsLocationModalOpen(false)} 
        onSave={(zip, radius, latitude, longitude) => {
          location.setLocation({ zip, radius, latitude, longitude });
          setIsLocationModalOpen(false);
        }}
        initialZip={location.zip}
        initialRadius={location.radius}
        initialLat={location.latitude}
        initialLng={location.longitude}
      />
      <CategoryModal 
        isOpen={isCategoryModalOpen} 
        onClose={() => setIsCategoryModalOpen(false)} 
        onSelect={(cat) => {
          console.log("Selected category:", cat);
          setIsCategoryModalOpen(false);
        }}
      />
    </div>
  );
};

export default App;
